import { Component, Enum, Label, lerp, _decorator } from 'cc';
import { getLocaleStringLength } from '../../Utils/Utility';

enum Type {
    Step,
    Gradient,
}

const { ccclass, property, requireComponent, executeInEditMode } = _decorator;

@ccclass('FontSizeConstraint')
class LabelPropertyConstraint {
    public static readonly EVENT_DITGIT_CHANGE = 'EVENT_DITGIT_CHANGE';

    @property
    public fontSize: number = 0;

    @property
    public lineHeight: number = 0;

    @property
    public scale: number = 1;

    @property
    public spacing: number = 0;

    @property
    private _digit: number = 0;

    public onDigitChange: (digit: number) => void = null;

    @property({ displayOrder: 0 })
    public get digit() {
        return this._digit;
    }
    private set digit(value: number) {
        if (this._digit === value) {
            return;
        }
        this._digit = value;
        this.onDigitChange?.(value);
    }

    public static lerp(from: LabelPropertyConstraint, to: LabelPropertyConstraint, digit: number): LabelPropertyConstraint {
        if (digit > from._digit || digit < to._digit) {
            throw new Error('digit is out of range');
        }
        const result = new LabelPropertyConstraint();
        const ratio = (digit - from._digit) / (to._digit - from._digit);
        result._digit = digit;
        result.fontSize = lerp(from.fontSize, to.fontSize, ratio);
        result.lineHeight = lerp(from.lineHeight, to.lineHeight, ratio);
        result.scale = lerp(from.scale, to.scale, ratio);
        result.spacing = lerp(from.spacing, to.spacing, ratio);
        return result;
    }
}

@ccclass('LabelPropertyAdjustor')
@requireComponent(Label)
@executeInEditMode
export default class LabelPropertyAdjustor extends Component {
    @property({ type: Enum(Type) })
    private _type: Type = Type.Step;

    @property([LabelPropertyConstraint])
    private readonly _constraints: LabelPropertyConstraint[] = [];

    @property
    private readonly updateFontSize: boolean = true;

    @property
    private readonly updateLineHeight: boolean = true;

    @property
    private readonly updateScale: boolean = true;

    @property
    private readonly updateSpacing: boolean = true;

    private _label: Label = null;

    private _previousDigit = 0;
    private _onLabelContentChangedCallback = this._onLabelContentChanged.bind(this);

    @property({ type: Enum(Type), displayOrder: 0 })
    private get type() {
        return this._type;
    }
    private set type(value: Type) {
        if (this._type === value) {
            return;
        }
        this._type = value;
        if (this._type === Type.Gradient && this._constraints.length < 2) {
            for (let index = this._constraints.length; index < 2; index++) {
                this._constraints.push(new LabelPropertyConstraint());
            }
        } else if (this._type === Type.Step && !this._constraints.length) {
            this._constraints.push(new LabelPropertyConstraint());
        }
    }

    // to prevent editor from modifying the array directly
    @property({ type: [LabelPropertyConstraint], displayOrder: 1 })
    private get constraints() {
        return [...this._constraints];
    }
    private set constraints(value: LabelPropertyConstraint[]) {
        this._constraints.length = 0;
        this._constraints.push(...value);
    }

    protected onLoad() {
        this._label = this.getComponent(Label);
        this._label.registerTextChangeCallback(this._onLabelContentChangedCallback);
        const sortConstraint = () => this._constraints.sort((a, b) => b.digit - a.digit);
        const temp = this._constraints.push;
        this._constraints.push = function push(this: LabelPropertyConstraint[], ...items: LabelPropertyConstraint[]) {
            items.forEach((item) => {
                item.onDigitChange = sortConstraint;
            });
            const result = temp.apply(this, items);
            sortConstraint();
            return result;
        };
        sortConstraint();
    }

    protected start() {
        this._onLabelContentChanged(this._label.string);
    }

    private _onLabelContentChanged(str: string) {
        const currentDigit = getLocaleStringLength(str.replace(/[.|,]/g, ''));
        if (currentDigit === this._previousDigit) {
            return str;
        }
        this._previousDigit = currentDigit;

        const constraintIndex = this._constraints.findIndex((constraint) => currentDigit >= constraint.digit);
        const constraint = constraintIndex === -1 ? this._constraints[this._constraints.length - 1] : this._constraints[constraintIndex];
        if (this.type === Type.Step || constraintIndex === -1 || constraintIndex === 0 || constraint.digit === currentDigit) {
            this._applyConstraint(constraint);
        } else {
            const previousConstraint = this._constraints[constraintIndex - 1];
            const newConstraint = LabelPropertyConstraint.lerp(previousConstraint, constraint, currentDigit);
            this._applyConstraint(newConstraint);
        }
        return str;
    }

    private _applyConstraint(constraint: LabelPropertyConstraint) {
        if (this.updateFontSize) {
            this._label.fontSize = constraint.fontSize;
        }
        if (this.updateLineHeight) {
            this._label.lineHeight = constraint.lineHeight;
        }
        if (this.updateScale) {
            this._label.node.setScale(constraint.scale, constraint.scale, 1);
        }
        if (this.updateSpacing) {
            this._label.spacingX = constraint.spacing;
        }
    }
}
